/**
 * useCreatorFeesQuery Hook
 *
 * React Query backed hook for fetching creator fees (BC + DAMM V2).
 * Powers the wallet dropdown and the Creator Fees widget. Both fetch
 * orchestration and totals math live in src/lib/creator/fees.ts so
 * this hook stays in sync with useCreatorFees (page hook).
 */

import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import BN from 'bn.js';
import { useMemo, useCallback } from 'react';
import {
  FeeMetricsClient,
  PoolFeeMetrics,
} from '@/lib/meteora/feeMetricsClient';
import type { DammV2PositionFees } from '@/lib/meteora/dammV2FeeReader';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { queryKeys, CACHE_TIERS } from '@/lib/queryClient';
import {
  CreatorFeeSnapshot,
  CreatorTokenWithDammV2,
  FeeFetchContext,
  computeClaimableTotals,
  fetchAllCreatorClaimableFees,
  fetchSingleBcMetric,
  fetchSingleDammV2Fee,
  getEnvironmentTag,
  getRpcProviderTag,
} from '@/lib/creator/fees';

export type CreatorToken = CreatorTokenWithDammV2;
export type { CreatorFeeSnapshot } from '@/lib/creator/fees';

export function useCreatorFeesQuery(creatorAddress: string | null) {
  const queryClient = useQueryClient();
  const { connected, walletAddress } = useTurnkeyWallet();

  const feeClient = useMemo(() => new FeeMetricsClient(getSolanaEndpoint()), []);

  const ctx = useMemo<FeeFetchContext>(
    () => ({
      hook: 'useCreatorFeesQuery',
      rpcProvider: getRpcProviderTag(),
      environment: getEnvironmentTag(),
    }),
    [],
  );

  const isOwnProfile =
    connected && walletAddress && creatorAddress === walletAddress;

  const query = useQuery<CreatorFeeSnapshot>({
    queryKey: queryKeys.creators.fees(creatorAddress || ''),
    queryFn: () => fetchAllCreatorClaimableFees(creatorAddress!, feeClient, ctx),
    enabled: !!creatorAddress,
    ...CACHE_TIERS.TIER_3,
    refetchInterval: 3 * 60 * 1000,
    retry: false,
    placeholderData: keepPreviousData,
  });

  const refresh = useCallback(() => {
    if (creatorAddress) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.creators.fees(creatorAddress),
      });
    }
  }, [queryClient, creatorAddress]);

  const refreshPool = useCallback(
    async (poolAddress: string) => {
      if (!creatorAddress || !query.data) return;

      const cached = query.data;
      const token = cached.tokens.find((t) => t.pool_address === poolAddress);

      let newBc: PoolFeeMetrics | null = null;
      try {
        newBc = await fetchSingleBcMetric(poolAddress, feeClient);
      } catch (err) {
        Sentry.captureException(err, {
          level: 'error',
          tags: {
            feature: 'creator-fees',
            hook: ctx.hook,
            op: 'refresh_bc_single',
            pool_address: poolAddress,
            rpc_provider: ctx.rpcProvider,
            environment: ctx.environment,
          },
        });
      }

      let newDamm: DammV2PositionFees | null | undefined;
      if (token?.damm_pool_address && token.creator_position_address) {
        try {
          newDamm = await fetchSingleDammV2Fee(
            token.damm_pool_address,
            token.creator_position_address,
            feeClient.getConnection(),
          );
        } catch (err) {
          newDamm = null;
          Sentry.captureException(err, {
            level: 'error',
            tags: {
              feature: 'creator-fees',
              hook: ctx.hook,
              op: 'refresh_dammv2_single',
              mint_address: token.mint_address,
              rpc_provider: ctx.rpcProvider,
              environment: ctx.environment,
            },
          });
        }
      }

      queryClient.setQueryData<CreatorFeeSnapshot>(
        queryKeys.creators.fees(creatorAddress),
        (old) => {
          if (!old) return old;
          const bcMetrics = new Map(old.bcMetrics);
          bcMetrics.set(poolAddress, newBc);

          let dammV2Fees = old.dammV2Fees;
          if (newDamm !== undefined && token) {
            dammV2Fees = new Map(old.dammV2Fees);
            dammV2Fees.set(token.mint_address, newDamm);
          }

          const { totalClaimable, claimablePoolsCount } = computeClaimableTotals(
            bcMetrics,
            dammV2Fees,
          );

          return {
            ...old,
            bcMetrics,
            dammV2Fees,
            totalClaimable,
            claimablePoolsCount,
          };
        },
      );
    },
    [creatorAddress, query.data, feeClient, queryClient, ctx],
  );

  return {
    tokens: query.data?.tokens || [],
    bcMetrics: query.data?.bcMetrics || new Map<string, PoolFeeMetrics | null>(),
    dammV2Fees: query.data?.dammV2Fees || new Map<string, DammV2PositionFees | null>(),
    totalClaimable: query.data?.totalClaimable || new BN(0),
    claimablePoolsCount: query.data?.claimablePoolsCount || 0,

    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,

    isOwnProfile,
    feeClient,

    refresh,
    refreshPool,
  };
}
